'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { withoutHeldAdClickIds } from 'lib/analytics/forwardedParams';
import {
  BoxOutline,
  Checkmark,
  ChevronRight,
  CircleTick,
  HandWithRing,
  InfoIcon,
  QuestionMarkCircleIcon,
  ShippingBox,
  ShoppingTrolley,
  WarningTriangle,
} from 'assets/icons';
import { PrimaryButton } from 'components/shared/PrimaryButton';
import type { RingBuyStepId } from 'components/ring-air/utils/buySteps';
import { RING_PRO_BUY_STEP_ORDER } from 'components/ring-pro/utils/buySteps';
import type { SequentialStepState } from 'lib/buy/sequentialSteps';
import BirthdayIcon from '../../../assets/birthday-icon.svg';
import { analytics } from 'lib/analytics/analytics';
// import { DeliveryManagement } from 'components/m1/DeliveryManagement';
import { isMarketingBannerActive } from 'components/shared/Header/MarketingBanner';
import { TravelChargerPricingModal } from 'components/shared/InfoModals/TravelChargerModal';
import { UhxPricingModal } from 'components/shared/InfoModals/uhx';
import { MaxSelectionContent } from 'components/shared/Buy/MaxSelectionContent';
import { getMaxConfig, hasMaxSelected, isMaxUsCopy } from 'content/products/max';
import { useMaxPlanCopy } from 'components/shared/Cart/hooks/useMaxPlanCopy';
import { OffersModalNewProvider } from 'components/shared/OffersModal';
// import { PlantTreesBanner } from 'components/shared/PlantTreesBanner';
import { VitaminAngelsBanner } from 'components/shared/VitaminAngelsBanner';
import { usePathname, useSearchParams } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  forwardRef,
  Fragment,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './index.module.css';
import { BuyPagePickup } from 'components/ring-pro/BestBuyPickup/BuyPagePickup';
import { SameDayPickupLine } from 'components/ring-pro/BestBuyPickup/SameDayPickupLine';
import {
  PickupEtaIcon,
  usePickupEta,
  usePickupReadyBy,
} from 'components/ring-pro/BestBuyPickup/usePickupEta';
import { scrollToElement } from 'components/ring-air/utils/highlightElement';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { ClueShopifyMap } from 'content/products/addons/clue';
import { RespiratoryShopifyMap } from 'content/products/addons/respiratory';
import {
  EngravingPrice,
  RingSizes,
  getEngravingKey,
} from 'content/products/ring/productMap/constants';
import {
  RingProColors,
  RingProChargerCaseColor,
  RingProVariants,
  RingProChargerCaseVariants,
  RingProCarouselData,
  getRingProColorTag,
  getRingProColorNameImages,
  AUTO_PAIR_CHARGER_WITH_RING,
  RING_COLOR_TO_CHARGER_MAP,
} from 'content/products/ring-pro';
import { ringProKeyT } from 'content/products/ring-pro/ringProKeyT';
import { GlobalUIContext } from 'contexts/GlobalUIContext';
import { CurrencySymbol, useFormatPrice, getCurrencySymbolFromString } from 'hooks/pricing/useFormattedPrice';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { useRadioGroupKeyboard } from 'hooks/dom/useRadioGroupKeyboard';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import useIsCnOPlus from 'hooks/pricing/useIsCnOPlus';
import { withIndiaEmiOfferPrice } from 'components/ring-air/utils/offersByRegion';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import {
  CustomImage,
  useModal,
  getShippingAndTaxDisclaimerMap,
  FSAHSAComponent,
  DotSpinner,
} from '../..';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { OffersCarousel, OffersDataType } from '../../ring-air/OffersCarousel';
import { RingProSelectionContext } from '../RingProSelectionContext';
import { SecondaryOfferDataType } from '../../ring-air/SecondaryOfferComponent';
import { H2 } from '../../ring-air/styleConstants';
import { RingProProductCarousel } from './RingProProductCarousel';
import { SplitPaymentsRow } from './SplitPaymentsRow';
import { TierProgressBar } from '../TierProgressBar';
import { HeroCategoriesMap } from 'components/shared/Cart/utils';
import {
  resolveHeroCategory,
  resolveVerifiedHeroProvider,
} from 'lib/commerce/heroes/heroVerification';
import { HeroOfferTag } from 'components/heroes/HeroOfferTag';
import { Currency } from 'utils/Currency';
import Image from 'next/image';
import { CnoProPricingModal } from 'components/shared/InfoModals/CnoPro';
import { CluePricingModal } from 'components/shared/InfoModals/Clue';
import { ClueLogo } from 'assets/icons/ClueLogo';
import { CircledArrow } from 'assets/icons/CircledArrow';
import { OtherProductSection } from './OtherProductsSection';
import CustomLink from 'components/shared/CustomLink';
import { usePartnerPolicy } from 'contexts/PartnerModeContext';
import { OptumBenefitBanner } from 'components/optum/OptumBenefitBanner';
import { GiftCardAppliedBanner } from 'components/gifting/redeem/GiftCardAppliedBanner';
import { RespiratoryLogo } from '../../../assets/icons/RespiratoryLogo';
import { useRetouchFlowContext } from 'components/shared/RetouchFlow';
import {
  isTradeInAvailable,
  TRADE_IN_DEVICE_NAMES,
  UHX_BONUS,
} from 'content/products/tradeIn';
const TradeInModal = dynamic(
  () =>
    import('components/shared/InfoModals/TradeIn').then((m) => m.TradeInModal),
  { ssr: false },
);
import {
  InfoModalContainer,
  InfoModalDescription,
  InfoModalTitle,
} from 'components/shared/InfoModals/layout';

const MaxPricingModal = dynamic(
  () =>
    import('components/shared/InfoModals/max').then((mod) => mod.MaxPricingModal),
  { ssr: false },
);

export const PanelContainer = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={`u-grid${className ? ` ${className}` : ''}`} {...props} />
);

export const LeftPanel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={className ? `${styles.leftPanel} ${className}` : styles.leftPanel}
    {...props}
  />
);

// Mobile-only product carousel slot. Rendered at all breakpoints so the SSR
// markup is correct without a JS width gate, and hidden on desktop via CSS
// (where the LeftPanel carousel takes over). Replaces the old
// `width < PHONE_MAX && ...` gate that left this absent/duplicated on first
// paint and caused the hero to pop in after hydration.
const MobileCarouselSection = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className
        ? `${styles.mobileCarouselSection} ${className}`
        : styles.mobileCarouselSection
    }
    {...props}
  />
);

export const RightPanel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className ? `${styles.rightPanel} ${className}` : styles.rightPanel
    }
    {...props}
  />
);

// Main Export Here 👇🏻
export const ProductSelectionSection = ({
  className,
  title,
  buySectionTitle,
  description,
  secondaryOfferData,
  offersData,
  defaultStrikedPrice,
  defaultOfferText,
  defaultOfferDiscount,
  bgColor,
}: {
    className?: string;
    title?: string;
    buySectionTitle?: string;
    description?: string;
    secondaryOfferData?: SecondaryOfferDataType[];
    offersData?: OffersDataType[];
    defaultStrikedPrice?: boolean;
    defaultOfferText?: string;
    defaultOfferDiscount?: string | number;
    bgColor?: string;
    offersBackgroundColor?: string;
    dealsBackgroundColor?: string;
  }) => {
    const { width: _width } = useWindowSize();
    const { chrome } = usePartnerPolicy();
    const globalUI = useContext(GlobalUIContext);
    const tCommon = useTranslations('common');
    const tRingPro = useTranslations('ring-pro');
    const { color, notServicable, uhxFirstOnly } = useContext(
      RingProSelectionContext,
    );
    void title;

    return (
      <section
        className={`${styles.root} u-grid${className ? ` ${className}` : ''}`}
        data-banner={isMarketingBannerActive() ? 'true' : 'false'}
        data-chrome={chrome}
        style={{ backgroundColor: bgColor ? bgColor : 'transparent' }}
      >
        <OffersModalNewProvider>
          <div className="content">
            {notServicable ? (
              <OtherProductSection
                title={
                  uhxFirstOnly
                    ? tRingPro('ring-pro.productSelectionSection.otherProductSection.uhxFirstTitle',
                      )
                    : tRingPro('ring-pro.productSelectionSection.otherProductSection.title',
                      )
                }
                subtitle={
                  uhxFirstOnly
                    ? tRingPro('ring-pro.productSelectionSection.otherProductSection.uhxFirstSubtitle',
                      )
                    : tCommon(
                        'productSelectionSection.otherProductSection.subtitle',
                      )
                }
              />
            ) : null}
            <PanelContainer
              style={{ filter: notServicable ? 'grayscale(1)' : undefined }}
            >
              <LeftPanel>
                <StickyPanel $headerVisible={globalUI?.headerVisible ?? true}>
                  <RingProProductCarousel
                    items={[
                      ...((color
                        ? RingProCarouselData[color]
                        : RingProCarouselData[RingProColors.GOLD]) ?? []),
                    ]}
                    selectedColor={color || RingProColors.GOLD}
                    sticky={true}
                    footerItems={[
                      {
                        title: (
                          <>
                            {tRingPro(
                              'ring-pro.productSelectionSection.carousel.upTo15DayBattery',
                            )}
                          </>
                        ),
                      },
                    ]}
                  />
                </StickyPanel>
              </LeftPanel>
              <RightPanel>
                <BuySection
                  buySectionTitle={buySectionTitle}
                  description={description}
                  secondaryOfferData={secondaryOfferData}
                  offersData={offersData}
                  defaultStrikedPrice={defaultStrikedPrice}
                  defaultOfferText={defaultOfferText}
                  defaultOfferDiscount={defaultOfferDiscount}
                />
              </RightPanel>
            </PanelContainer>
          </div>
        </OffersModalNewProvider>
      </section>
    );
};

const StickyPanel = ({
  $headerVisible,
  className,
  ...props
}: { $headerVisible: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={
      className ? `${styles.stickyPanel} ${className}` : styles.stickyPanel
    }
    data-header-visible={$headerVisible ? 'true' : 'false'}
    {...props}
  />
);

const SizeInfoModal = ({ className }: { className?: string }) => {
  const t = useTranslations('ring');
  const tCommon = useTranslations('common');
  const tRingPro = useTranslations('ring-pro');
  const { width } = useWindowSize();
  return (
    <div
      className={
        className ? `${styles.sizeInfoModal} ${className}` : styles.sizeInfoModal
      }
    >
      {/* <h2>How Ring Sizing Works</h2> */}
      <h2>{t('ring.productSelection.sizingSection.modalNew.title')}</h2>
      <p>
        {tRingPro('ring-pro.productSelectionSection.sizingModal.subtitle',
        )}
      </p>
      <ol>
        <li>
          <ShoppingTrolley />
          <h5>
            {/* <span>Step 1:</span> Order your ring */}
            <span>
              {t('ring.productSelection.sizingSection.modalNew.stepA.step')}
            </span>{' '}
            {t('ring.productSelection.sizingSection.modalNew.stepA.title')}
          </h5>
          <p>
            {/* Place your order for the ring. A complimentary sizing kit is
            automatically included with every purchase unless you specifically
            opt out of it. */}
            {t('ring.productSelection.sizingSection.modalNew.stepA.subTitle')}
          </p>
        </li>
        <li>
          <BoxOutline fill="#000000" />
          <h5>
            {/* <span>Step 2:</span> Await the free ring sizing kit delivery */}
            <span>
              {t('ring.productSelection.sizingSection.modalNew.stepB.step')}
            </span>{' '}
            {t('ring.productSelection.sizingSection.modalNew.stepB.title')}
          </h5>
          <p>
            {/* We expedite your sizing kit, ensuring it&apos;s shipped within 24
            hours after you place your order. The kit contains a range of sample
            rings to help determine your ideal fit. This kit is yours to keep
            and share. */}
            {t('ring.productSelection.sizingSection.modalNew.stepB.subTitle')}
          </p>
        </li>
        <li>
          <HandWithRing />
          <h5>
            {/* <span>Step 3:</span> Find your perfect ring size */}
            <span>
              {t('ring.productSelection.sizingSection.modalNew.stepC.step')}
            </span>{' '}
            {t('ring.productSelection.sizingSection.modalNew.stepC.title')}
          </h5>
          <p>
            {/* Use the sample rings to identify your most comfortable size. For
            optimal results, watch the video guide or read the step-by-step
            guide{' '} */}
            {t('ring.productSelection.sizingSection.modalNew.stepC.subTitle')}{' '}
            <CustomLink
              target={'_blank'}
              href="/sizing"
              style={{ textDecoration: 'underline' }}
            >
              {/* here */}
              {t(
                'ring.productSelection.sizingSection.modalNew.stepC.HERE',
              )}{' '}
            </CustomLink>
            . {/* Take time to ensure the best fit. */}
            {t('ring.productSelection.sizingSection.modalNew.stepC.endText')}
          </p>
        </li>
        <li>
          <CircleTick />
          <h5>
            {/* <span>Step 4:</span> Submit your ring size */}
            <span>
              {t('ring.productSelection.sizingSection.modalNew.stepD.step')}
            </span>{' '}
            {t('ring.productSelection.sizingSection.modalNew.stepD.title')}
          </h5>
          <p>
            {/* After confirming your ideal size, submit it through the Ultrahuman
            app or via the order tracking link. We&apos;ll then process your
            order and ship your ring. */}
            {t('ring.productSelection.sizingSection.modalNew.stepD.subTitle')}
          </p>
        </li>
      </ol>
      <CustomLink
        className="video"
        target="_blank"
        href="https://www.youtube.com/watch?v=t9ca-Lq70q4"
      >
        <CustomImage
          width={540}
          height={270}
          alt={tCommon(
            'productSelectionSection.customImage.alt.sizingKitVideo',
          )}
          src={
            width < 900
              ? 'web_v2/ring-buy/thumbnail_desktop.png'
              : 'web_v2/ring-buy/thumbnail_mobile.png'
          }
        />
        {tCommon('productSelectionSection.link.watchOurVideo')}
      </CustomLink>
      <hr />

      <p className="disclaimer">
        {/* The right fit ensures both comfort for continuous wear and accurate data
        tracking. If you need any assistance, our team is here to help at every
        step. Reach out to us at{' '} */}
        {t('ring.productSelection.sizingSection.modalNew.disclaimer')}{' '}
        <a href="mailto:support@ultrahuman.com">support@ultrahuman.com</a>.
      </p>
    </div>
  );
};

const LearnMoreButton = ({
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => {
    const tCommon = useTranslations('common');
    return (
      <button
        className={
          className
            ? `${styles.learnMoreButton} ${className}`
            : styles.learnMoreButton
        }
        {...props}
      >
        {tCommon('productSelectionSection.button.learnMore')}
        <CircledArrow />
      </button>
    );
};

type ChargerCaseTab = 'gold' | 'black';

type RingColorSlug = 'gold' | 'silver' | 'black' | 'titanium';

const RING_COLOR_TO_SLUG: Record<string, RingColorSlug> = {
  GOLD: 'gold',
  SILVER: 'silver',
  BLACK: 'black',
  TITANIUM: 'titanium',
};

const ChargerLearnMoreModal = ({
  className,
  ringColor,
}: {
  className?: string;
  ringColor?: RingProColors;
}) => {
    const _tCommon = useTranslations('common');
    const tRingPro = useTranslations('ring-pro');
    const ringColorSlug: RingColorSlug = ringColor
      ? RING_COLOR_TO_SLUG[ringColor] ?? 'gold'
      : 'gold';
    const chargerFromRing: ChargerCaseTab = ringColor
      ? (RING_COLOR_TO_CHARGER_MAP[ringColor].toLowerCase() as ChargerCaseTab)
      : 'gold';

    const [activeTab, setActiveTab] = useState<ChargerCaseTab>(
      AUTO_PAIR_CHARGER_WITH_RING ? chargerFromRing : 'gold',
    );
    const goldTabId = 'charger-learn-more-tab-gold';
    const blackTabId = 'charger-learn-more-tab-black';
    const panelId = 'charger-learn-more-panel';

    const handleTabKeyDown = useCallback((e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveTab((prev) => (prev === 'black' ? 'gold' : prev));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveTab((prev) => (prev === 'gold' ? 'black' : prev));
      }
    }, []);

    const goldSelected: 'true' | 'false' =
      activeTab === 'gold' ? 'true' : 'false';
    const blackSelected: 'true' | 'false' =
      activeTab === 'black' ? 'true' : 'false';

    const displayColor = AUTO_PAIR_CHARGER_WITH_RING
      ? chargerFromRing
      : activeTab;
    const displayLabel =
      displayColor === 'gold'
        ? tRingPro('ring-pro.productSelectionSection.chargerModal.gold')
        : tRingPro('ring-pro.productSelectionSection.chargerModal.black',
          );

    return (
      <div
        className={
          className
            ? `${styles.chargerLearnMoreModal} ${className}`
            : styles.chargerLearnMoreModal
        }
        role="dialog"
        aria-modal="true"
        aria-labelledby="charger-learn-more-title"
      >
        <InfoModalContainer>
          <InfoModalTitle>
            <h3 id="charger-learn-more-title">
              {tRingPro('ring-pro.productSelectionSection.chargerModal.title',
              )}
            </h3>
          </InfoModalTitle>
          <InfoModalDescription>
            <p>
              {tRingPro('ring-pro.productSelectionSection.chargerModal.description',
              )}
            </p>
          </InfoModalDescription>
          {!AUTO_PAIR_CHARGER_WITH_RING && (
            <div
              className="charger-modal-tabs"
              role="tablist"
              aria-label={tRingPro('ring-pro.productSelectionSection.chargerModal.tabsAriaLabel',
              )}
              tabIndex={0}
              onKeyDown={handleTabKeyDown}
            >
              <button
                type="button"
                role="tab"
                id={goldTabId}
                aria-selected={goldSelected}
                aria-controls={panelId}
                tabIndex={activeTab === 'gold' ? 0 : -1}
                className={activeTab === 'gold' ? 'active' : undefined}
                onClick={() => setActiveTab('gold')}
              >
                {tRingPro('ring-pro.productSelectionSection.chargerModal.goldChargingCase',
                )}
              </button>
              <button
                type="button"
                role="tab"
                id={blackTabId}
                aria-selected={blackSelected}
                aria-controls={panelId}
                tabIndex={activeTab === 'black' ? 0 : -1}
                className={activeTab === 'black' ? 'active' : undefined}
                onClick={() => setActiveTab('black')}
              >
                {tRingPro('ring-pro.productSelectionSection.chargerModal.blackChargingCase',
                )}
              </button>
            </div>
          )}
          <div
            id={panelId}
            role={AUTO_PAIR_CHARGER_WITH_RING ? undefined : 'tabpanel'}
            aria-labelledby={
              !AUTO_PAIR_CHARGER_WITH_RING
                ? activeTab === 'gold'
                  ? goldTabId
                  : blackTabId
                : undefined
            }
          >
            <div className="charger-modal-img">
              <img
                src={getCompressedAssetUrl(
                  `/web_v2/ring-pro/buy-v2/case/${displayColor}/case-1.png/w_1200`,
                )}
                alt={tRingPro('ring-pro.productSelectionSection.chargerModal.altCase',
                  { label: displayLabel },
                )}
                loading="lazy"
              />
            </div>
            <div className="charger-modal-img">
              <img
                src={getCompressedAssetUrl(
                  `/web_v2/ring-pro/buy-v2/case/${displayColor}/r-${ringColorSlug}-1.png/w_1200`,
                )}
                alt={tRingPro('ring-pro.productSelectionSection.chargerModal.altCaseWithRing',
                  { label: displayLabel },
                )}
                loading="lazy"
              />
            </div>
            <p className="charger-modal-caption">
              {tRingPro('ring-pro.productSelectionSection.chargerModal.caption',
              )}
            </p>
          </div>
          <div className="charger-modal-features">
            {[
              {
                icon: getCompressedAssetUrl(
                  '/web_v2/site-static/ring-pro/icons/icon-3.svg',
                ),
                title: tRingPro('ring-pro.productSelectionSection.chargerModal.features.universal.title',
                ),
                description: tRingPro('ring-pro.productSelectionSection.chargerModal.features.universal.description',
                ),
              },
              {
                icon: getCompressedAssetUrl(
                  '/web_v2/site-static/ring-pro/icons/icon-wave.svg',
                ),
                title: tRingPro('ring-pro.productSelectionSection.chargerModal.features.speaker.title',
                ),
                description: tRingPro('ring-pro.productSelectionSection.chargerModal.features.speaker.description',
                ),
              },
              {
                icon: getCompressedAssetUrl(
                  '/web_v2/site-static/ring-pro/icons/icon-wireless.svg',
                ),
                title: tRingPro('ring-pro.productSelectionSection.chargerModal.features.findCase.title',
                ),
                description: tRingPro('ring-pro.productSelectionSection.chargerModal.features.findCase.description',
                ),
              },
              {
                icon: getCompressedAssetUrl(
                  '/web_v2/site-static/ring-pro/icons/icon-1.svg',
                ),
                title: tRingPro('ring-pro.productSelectionSection.chargerModal.features.lighting.title',
                ),
                description: tRingPro('ring-pro.productSelectionSection.chargerModal.features.lighting.description',
                ),
              },
              {
                icon: getCompressedAssetUrl(
                  '/web_v2/site-static/ring-pro/icons/icon-4.svg',
                ),
                title: tRingPro('ring-pro.productSelectionSection.chargerModal.features.wireless.title',
                ),
                description: tRingPro('ring-pro.productSelectionSection.chargerModal.features.wireless.description',
                ),
              },
              {
                icon: getCompressedAssetUrl(
                  '/web_v2/site-static/ring-pro/icons/icon-2.svg',
                ),
                title: tRingPro('ring-pro.productSelectionSection.chargerModal.features.other.title',
                ),
                description: tRingPro('ring-pro.productSelectionSection.chargerModal.features.other.description',
                ),
              },
            ].map((feature, i) => (
              <div className="charger-modal-feature" key={i}>
                <img src={feature.icon} alt="" width={36} height={36} />
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </InfoModalContainer>
      </div>
    );
};

const Section = forwardRef<
    HTMLDivElement,
    {
      title?: React.ReactNode | string;
      subTitle?: React.ReactNode | string;
      subText?: React.ReactNode | string;
      openInfoModal?: () => void;
      /** Overrides the default InfoIcon glyph for this section only. */
      infoIcon?: React.ReactNode;
      openInfoModalText?: React.ReactNode | string;
      children: React.ReactNode;
      childClass: string;
      sectionClass?: string;
      state?: SequentialStepState;
      /** Heading shown while locked for sections that carry none (order summary). */
      lockedTitle?: React.ReactNode | string;
      disableScrollToScreen?: boolean;
      hsaFsaBadge?: boolean;
    }
  >(
    (
      {
        title: titleProp,
        subTitle,
        subText,
        openInfoModal,
        infoIcon,
        openInfoModalText,
        children,
        childClass,
        sectionClass,
        state = 'complete',
        lockedTitle,
        hsaFsaBadge,
      },
      ref,
    ) => {
      const tRingPro = useTranslations('ring-pro');
      const title =
        titleProp ?? (state === 'locked' ? lockedTitle : undefined);
      // Icon-only info buttons were named by the bare heading ("Size."), which
      // reads like the heading itself; say what the button opens. Buttons with
      // visible text keep that text as their name (WCAG 2.5.3).
      const infoAriaLabel =
        typeof title !== 'string' || openInfoModalText
          ? undefined
          : tRingPro('ring-pro.productSelectionSection.sectionInfoAriaLabel', {
              title: title.trim().replace(/\.$/, ''),
            });
      return (
        <div
          ref={ref}
          className={`${styles.section} ${sectionClass ?? ''}`}
          data-state={state}
          inert={state === 'locked'}
          data-has-modal-text={openInfoModalText ? 'true' : 'false'}
        >
          {title ? (
            <div className="section-row-title">
              <h5>
                {title} <span className="subtitle">{subTitle}</span>
              </h5>
              {openInfoModal ? (
                <button
                  type="button"
                  onClick={openInfoModal}
                  aria-label={infoAriaLabel}
                  aria-haspopup="dialog"
                >
                  {!openInfoModalText ? (
                    infoIcon ?? (
                      <InfoIcon
                        style={{
                          width: '1.6rem',
                          height: 'auto',
                          marginTop: '1px', //change this when changing the font sizes
                        }}
                        fill="#000"
                      />
                    )
                  ) : null}
                  {openInfoModalText ? openInfoModalText : null}
                </button>
              ) : null}
            </div>
          ) : null}
          {subText ? <p className="section-row-sub-title">{subText}</p> : null}
          {hsaFsaBadge ? (
            <div className="hsa-fsa-badge">
              <FSAHSAComponent />
            </div>
          ) : null}
          <div className={`section-row-content ${childClass}`}>{children}</div>
        </div>
      );
    },
  );

Section.displayName = 'Section';

const PowerPlugSubsection = ({
    title,
    description,
    image,
    children,
    className,
  }: {
    title: string;
    description?: string;
    image?: string;
    children: React.ReactNode;
    className?: string;
  }) => {
    return (
      <div
        className={
          className
            ? `${styles.powerPlugSubsection} ${className}`
            : styles.powerPlugSubsection
        }
      >
        <div className="subsection-header">
          {image ? (
            <div className="image-container">
              <CustomImage src={image} alt={title} height="64" width="64" />
            </div>
          ) : null}
          <div className="text-container">
            <h4 className="subsection-title">{title}</h4>
            {description ? (
              <p className="subsection-description">{description}</p>
            ) : null}
          </div>
        </div>
        <div className="ultrahumanx-content">{children}</div>
      </div>
    );
};

const LargePromptBtnStyledContent = ({
  className,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <button
    className={
      className ? `${styles.largePrompt} ${className}` : styles.largePrompt
    }
    {...props}
  >
    {children}
  </button>
);

const BuySection = ({
    className,
    secondaryOfferData,
    offersData,
    description,
    buySectionTitle = 'Ring PRO',
    defaultStrikedPrice = false,
    defaultOfferText,
    defaultOfferDiscount,
  }: {
    className?: string;
    description?: string;
    buySectionTitle?: React.ReactNode;
    secondaryOfferData?: SecondaryOfferDataType[];
    offersData?: OffersDataType[];
    defaultStrikedPrice?: boolean;
    defaultOfferText?: string;
    defaultOfferDiscount?: number | string;
  }) => {
    const { region: country } = useRegionLocale();
    const searchParams = useSearchParams();
    const pathname = usePathname();
    // Verified heroes (US): drop the self-declaration disclaimers + relabel the discount.
    const heroCategory = resolveHeroCategory(searchParams) ?? '';
    const verifiedBy = resolveVerifiedHeroProvider(searchParams, country);
    const isIdmeDiscount = verifiedBy === 'idme';
    const isGovxDiscount = verifiedBy === 'govx';
    const isVerifiedHeroDiscount = isGovxDiscount || isIdmeDiscount;
    const RingSelectionClient = useContext(RingProSelectionContext);
    const { store, getCampaignForProduct } = useCartStore(
      useShallow((s) => ({
        store: s.store,
        getCampaignForProduct: s.getCampaignForProduct,
      })),
    );
    const { resetCounter: resetRetouchCounter } = useRetouchFlowContext();
    const ringCampaign = getCampaignForProduct('ring');
    const { width: _width } = useWindowSize();
    const ModalClient = useModal();
    const {
      color,
      setColor,

      size,
      setSize,

      chargerCaseColor,

      maxCart,
      setMaxCart,

      defaultCurrency: currency,

      engraving,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setEngraving,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      engravingMessage,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      setEngravingMessage,

      checkout,
      isLoading,
      basePrice,
      price,
      effectivePrice,
      currencySymbol,
      ultraHumanPrice,
      chargerPrice,
      shippingEstimate,
      affiliateDetails,
      affiliateDiscount,
      referral,
      setChargerCaseColor,
      sectionRefs,
      stepStateOf,
      deliveryStepVisible,
      canAddToCart,
      colorChosen,
      tradeInDeclined,
      declineTradeIn,
      effectiveDiscount,
      cnoProCart,
      clueCart,
      setClueCart,
      respiratoryCart,
      isLuminaryFlow,
      luminaryDiscount,
      priceDrop,
      notServicable,
      notServicableMessage,
      isOpenSizeDisabled,
      tradeInDevice,
      setTradeInDevice,
      tradeInSerialNumber,
      setTradeInSerialNumber,
      tradeInCredit,
      tradeInMaxCredit,
    } = RingSelectionClient;
    // Pickup chosen: the Best Buy line stands in for every "Ships by".
    const pickupEta = usePickupEta({ store, color, size, engraving });
    // Tag only beside "Ready by <date> at Best Buy <name>".
    const pickupReadyBy = usePickupReadyBy({ store, color, size, engraving });
    const tPickup = useTranslations('ring-pro');

    const showTradeIn =
      isTradeInAvailable(
        store as ShopifyStore,
        searchParams?.get('affiliateCode'),
      ) && tradeInMaxCredit > 0;

    if (!defaultStrikedPrice) {
      defaultStrikedPrice = basePrice > price;
    }

    const effectiveStoreForAffiliate = getEffectiveStoreForDiscount(store);

    // Add/remove stores here to control installment price display (e.g. "or $88/mo ±")
    const INSTALLMENT_CONFIG: Partial<
      Record<ShopifyStore, { months: number; label: string }>
    > = {
      [ShopifyStore.US]: { months: 4, label: 'mo' },
      [ShopifyStore.AE]: { months: 4, label: 'mo' },
      [ShopifyStore.EU]: { months: 4, label: 'mo' },
      [ShopifyStore.GB]: { months: 4, label: 'mo' },
      [ShopifyStore.IN]: { months: 24, label: 'mo' },
    };

    const t = useTranslations('ring');
    const policy = usePartnerPolicy();
    const installmentConfig = store
      ? INSTALLMENT_CONFIG[store as ShopifyStore]
      : null;
    // Ring price after every ring-level discount. luminaryDiscount is its own
    // term because the Luminary flow suppresses the discounts that feed
    // effectiveDiscount rather than competing with them.
    const ringPriceAfterDiscounts =
      (effectiveDiscount.serverAmount > 0
        ? price - effectiveDiscount.affiliateAmount
        : price - effectiveDiscount.amount) - luminaryDiscount;
    const installmentPrice = installmentConfig
      ? Math.ceil(ringPriceAfterDiscounts / installmentConfig.months)
      : null;

    // IN: the offers list ships an EMI label derived from the Ring AIR price.
    // Re-derive it from THIS product's live discounted price, as main does —
    // otherwise the PRO buy page advertises the AIR monthly instalment.
    const resolvedOffersData = useMemo(
      () =>
        store === ShopifyStore.IN
          ? (withIndiaEmiOfferPrice(offersData, t, ringPriceAfterDiscounts) ??
            offersData)
          : offersData,
        [store, offersData, t, ringPriceAfterDiscounts],
    );

    const {
      showColorRef,
      showSizeRef,
      showChargerRef,
      showTradeInRef,
      coverageRef,
      powerplugRef,
      cartSummaryRef,
    } = sectionRefs;

    const isClueFlow = false;
    const isRespiratoryFlow = false;
    const isUSLocale = country?.toUpperCase() === 'US';
    const { getValue } = useIsCnOPlus();
    const isOvusenseFlow = false;

    // Determine ring promo text based on country
    // const ringPromoText =
    //   (router.query.locale as string)?.toUpperCase() === 'AU' ||
    //   country?.toUpperCase() === 'AU'
    //     ? 'Black Friday Offer Applied'
    //     : 'Special Offer Applied';

    //FOR HSA/FSA redirects
    useEffect(() => {
      const scrollTimeout = setTimeout(() => {
        if (
          powerplugRef?.current &&
          searchParams?.get('ps') === 'cardio-powerplug' &&
          store === ShopifyStore.US
        ) {
          powerplugRef.current.scrollIntoView({
            behavior: 'smooth',
          });
        }
      }, 100);

      return () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
      };
    }, []);

    const openSizeInfoModal = () => {
      ModalClient.set(<SizeInfoModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openMaxInfoModal = () => {
      ModalClient.set(<MaxPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    /** The nested "UltrahumanX Coverage included" row explains UHX, not MAX. */
    const openUhxInfoModal = () => {
      ModalClient.set(<UhxPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };
    const maxConfig = getMaxConfig(store);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const openVoyagerCharger = () => {
      ModalClient.set(<TravelChargerPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openCnoPro = () => {
      ModalClient.set(<CnoProPricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const openClueModal = () => {
      ModalClient.set(<CluePricingModal />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };


    const openChargerLearnMoreModal = () => {
      ModalClient.set(<ChargerLearnMoreModal ringColor={color ?? undefined} />);
      ModalClient.setPortal(true);
      ModalClient.show();
    };

    const handleCheckout = () => {
      if (isLoading) return;
      analytics.track(
        'select_content',
        {
          content_type: 'cta',
          content_id: 'proceed_checkout',
          placement: 'summary',
          product: 'ring_pro',
        },
        'CLICK: Product Selection, Add to Cart',
      );
      resetRetouchCounter(); // Reset retouch flow counter on Add to Cart
      checkout();
      ModalClient.hide();
    };

    const hasNumericSize = size != null && size !== RingSizes.OPEN;
    const [manualSizeSelectVisible, setManualSizeSelectVisible] = useState(
      isOpenSizeDisabled === true || hasNumericSize,
    );
    const isManualSizeExpanded =
      isOpenSizeDisabled || manualSizeSelectVisible || hasNumericSize;
    const [isTradeInModalVisible, setIsTradeInModalVisible] = useState(false);
    // Swatch under the pointer (or keyboard focus): its name previews in the label.
    const [hoveredColor, setHoveredColor] = useState<RingProColors | null>(null);
    const onColorKeyDown = useRadioGroupKeyboard();
    const openTradeInModal = () => setIsTradeInModalVisible(true);

    const handleManualSizeSelectShow = () => {
      if (isOpenSizeDisabled) {
        setManualSizeSelectVisible(true);
        return;
      }
      if (isManualSizeExpanded) {
        setManualSizeSelectVisible(false);
        if (hasNumericSize) setSize(null);
      } else {
        setManualSizeSelectVisible(true);
        if (size === RingSizes.OPEN) setSize(null);
      }
    };

    // Best Buy pickup needs a ring size. Kit or no size: clear the kit (as the
    // "I know my size" card does) and land on the first size. A size Best Buy
    // doesn't stock: land on the current size without clearing it.
    const chooseSizeForPickup = () => {
      setManualSizeSelectVisible(true);
      if (size === RingSizes.OPEN) setSize(null);
      const keepSize = size !== null && size !== RingSizes.OPEN;
      requestAnimationFrame(() => {
        if (!showSizeRef) return;
        scrollToElement(showSizeRef);
        const sizes = showSizeRef.current;
        const target = keepSize
          ? sizes?.querySelector<HTMLButtonElement>('.sizes button.active')
          : sizes?.querySelector<HTMLButtonElement>(
              '.sizes button:not(:disabled)',
            );
        target?.focus({ preventScroll: true });
      });
    };

    // Best Buy doesn't stock this color in this size: land on the color
    // section with the active swatch focused, the selection left as is.
    const chooseColorForPickup = () => {
      requestAnimationFrame(() => {
        if (!showColorRef) return;
        scrollToElement(showColorRef);
        showColorRef.current
          ?.querySelector<HTMLElement>(`[data-testid="ring-color-${color}"]`)
          ?.focus({ preventScroll: true });
      });
    };

    const tCart = useTranslations('cart');
    const maxPlanCopy = useMaxPlanCopy();
    const tCommon = useTranslations('common');
    const tRingPro = useTranslations('ring-pro');
    const RingAirColorNameImages = getRingProColorNameImages(
      ringProKeyT(t, tRingPro),
    );
    const isCnoProActive = true;

    // this is used to scoll from powerplugs
    // const element = document.getElementById('powerplugs');
    // const scrolledRef = useRef<boolean>(false);
    // useEffect(() => {
    //   if (
    //     router.asPath.includes('#cardio') &&
    //     !scrolledRef.current &&
    //     element
    //   ) {
    //     coverageRef?.current?.scrollIntoView({ behavior: 'smooth' });
    //     scrolledRef.current = true;
    //   }
    // }, [router, element]);

    const sizeDisclaimerViewedRef = useRef<boolean>(false);

    const handleSizeDisclaimer = () => {
      if (sizeDisclaimerViewedRef.current || isOpenSizeDisabled) return;


      ModalClient.setCloseBtnHidden(true);
      ModalClient.set(
        <div
          style={{
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
          }}
        >
          <h2
            style={{
              fontWeight: '500',
              fontSize: '24px',
              letterSpacing: '-0.3px',
            }}
          >
            <WarningTriangle
              fill="#F2B534"
              style={{
                height: '17px',
                width: 'auto',
                marginRight: '8px',
              }}
            />
            {/* Our ring sizes are unique */}
            {t('ring.productSelection.sizeDisclaimerNew.title')}
          </h2>
          <p
            style={{
              fontSize: '19px',
              marginTop: '16px',
              lineHeight: '130%',
              color: 'rgba(0,0,0,0.75)',
              maxWidth: '100%',
              letterSpacing: '-0.3px',
            }}
          >
            {/* Ultrahuman Rings have custom sizes, different from standard smart
            rings. The right fit ensures accurate data tracking. */}
            {t('ring.productSelection.sizeDisclaimerNew.content.a')}
            <br />
            <br />
            {/* If you know your size, proceed confidently. If not, get a free
            sizing kit (shipped within 24 hours) for the perfect fit. Guessing
            wrong costs the planet. */}
            {t('ring.productSelection.sizeDisclaimerNew.content.b')}
          </p>
          <div className={styles.disclaimerButtonContainer}>
            <button className={styles.disclaimerButton}
              onClick={() => {
                setSize(RingSizes.OPEN);
                ModalClient.hide();
                ModalClient.set(null);
                setManualSizeSelectVisible(false);
              }}
            >
              {/* Get free sizing kit */}
              {t('ring.productSelection.sizeDisclaimerNew.btnTextA')}
            </button>

            <button
              onClick={() => {
                sizeDisclaimerViewedRef.current = true;
                ModalClient.hide();
                ModalClient.set(null);
                analytics.track(`CLICK: Manual size selection`, {
                  size: size ?? null,
                });
              }}
              className={`${styles.disclaimerButton} secondary gtm`}
              data-buttontype="Manual Size Selection Agree"
              data-product="Manual Size Selection Agree"
            >
              {/* I know my size */}
              {t('ring.productSelection.sizeDisclaimerNew.btnTextB')}
            </button>
          </div>
        </div>,
      );
      ModalClient.show();
    };

    const formatPriceWithComma = useFormatPrice();

    useEffect(() => {
      if (
        searchParams?.get('affiliate') ||
        searchParams?.get('affiliateCode')
      ) {
        setMaxCart(false);
      }
    }, [searchParams]);

    const heroOfferTag = (icon: React.ReactNode, label: string) => (
      <HeroOfferTag verifiedBy={verifiedBy} icon={icon} label={label} />
    );

    const heroCategoryOfferTagMap = {
      [HeroCategoriesMap.ATHLETES]: heroOfferTag(
        <CustomImage
          width={24}
          height={24}
          alt="athlete-icon"
          src={'web_v2/hero/hero-icons-green/Athlete.svg'}
        />,
        tCommon('productSelectionSection.content.athleteSavingsApplied'),
      ),
      [HeroCategoriesMap.SCIENTIST]: heroOfferTag(
        <CustomImage
          width={24}
          height={24}
          alt="science-icon"
          src={'web_v2/hero/hero-icons-green/Science.svg'}
        />,
        tCommon('productSelectionSection.content.scienceSavingsApplied'),
      ),
      [HeroCategoriesMap.HEALTHCARE]: heroOfferTag(
        <CustomImage
          width={24}
          height={24}
          alt="healthcare-icon"
          src={'web_v2/hero/hero-icons-green/Healthcare.svg'}
        />,
        tCommon('productSelectionSection.content.healthcareSavingsApplied'),
      ),
      [HeroCategoriesMap.EDUCATION]: heroOfferTag(
        <CustomImage
          width={24}
          height={24}
          alt="university-icon"
          src={'web_v2/hero/hero-icons-green/University.svg'}
        />,
        tCommon('productSelectionSection.content.educationSavingsApplied'),
      ),
      [HeroCategoriesMap.VETERANS]: heroOfferTag(
        <CustomImage
          width={28}
          height={28}
          alt="veteran-icon"
          src={'web_v2/hero/hero-icons-green/Veteran.svg'}
        />,
        tCommon('productSelectionSection.content.militarySavingsApplied'),
      ),
    };

    // const ColorSelectionChip = styled.div`
    //   display: flex;
    //   align-items: center;
    //   gap: 8px;
    //   padding: 8px 16px;
    //   // background: #f5f5f5;
    //   font-size: 14px;
    //   border-radius: 12px;
    //   color: rgba(0, 0, 0, 0.5);
    //   margin-top: 8px;

    //   strong {
    //     font-weight: 500;
    //     color: black;
    //   }
    // `;


    const engravingStrikePrice =
      engraving && store ? EngravingPrice[store as ShopifyStore] ?? 0 : 0;
    const strikeBasePrice = basePrice + engravingStrikePrice;

    const hasOfferContent =
      effectiveDiscount.type === 'affiliate' ||
      effectiveDiscount.type === 'server+affiliate' ||
      (effectiveDiscount.type === 'referral' && !!referral) ||
      (effectiveDiscount.type === 'hero' &&
        !!heroCategoryOfferTagMap[heroCategory]) ||
      (effectiveDiscount.serverAmount > 0 &&
        !!effectiveDiscount.priceTagText) ||
      (!!isClueFlow && !!country && !!store) ||
      (!!isRespiratoryFlow && !!country && !!store) ||
      (!!isOvusenseFlow && !!country && !!store && !!isCnoProActive) ||
      effectiveDiscount.type === 'birthday' ||
      effectiveDiscount.type === 'ringPromo' ||
      (!!defaultOfferText && !(affiliateDetails || referral));

    const NotServicableMessage = notServicable ? (
      notServicableMessage ? (
        <div className={styles.notServicableMessageContainer}>
          <WarningTriangle
            fill={'rgba(0, 0, 0, 0.6)'}
            style={{ width: '24px', height: '24px' }}
          />
          {notServicableMessage}
        </div>
      ) : (
        <div
          className={styles.notServicableMessageContainer}
          style={{
            justifyContent: 'center',
            fontWeight: '500',
            fontSize: '1.6rem',
            color: 'rgba(0, 0, 0, 0.6)',
            marginBottom: '24px',
          }}
        >
          {tCommon('productSelectionSection.content.currentlyUnavailable')}
        </div>
      )
    ) : null;

    // Early return if store is not yet initialized
    if (!store) {
      return null;
    }

    const buildColorHref = (c: RingProColors) => {
      const p = withoutHeldAdClickIds(searchParams?.toString() ?? '');
      p.set('color', c);
      return `?${p.toString()}`;
    };
    // No history.replaceState here: prod's swatches are plain buttons with no URL
    // write, and a URL write per colour fires GA4's history-change page_view —
    // inflating the variant arm. Deep links still work: state inits from ?color=.
    const selectColor = (c: RingProColors) => {
      setColor(c);
    };

    const ColorSelectionSection = (
      <Section
        title={t('ring.productSelection.colorSectionTitle')}
        subTitle={t('ring.productSelection.colorSectionSubTitle')}
        childClass="color-section"
        state={stepStateOf('color-picker')}
        ref={showColorRef}
        disableScrollToScreen={true}
        sectionClass="color-section"
      >
        <>
          {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus -- focus lives on the roving-tabindex radios inside the group */}
          <div
            className="colors"
            role="radiogroup"
            aria-label={t('ring.productSelection.colorSectionTitle')}
            onKeyDown={onColorKeyDown}
          >
            {Object.values(RingProColors).map((element, index) => {
              const tag = getRingProColorTag(element as RingProColors, store);
              const isColorSelected = !!colorChosen && element == color;
              const hasCheckedColor = !!colorChosen && !!color;
              const colorName = RingAirColorNameImages[element].name;

              return (
                <a
                  href={buildColorHref(element as RingProColors)}
                  style={{
                    transitionDelay: `${index * 0.05}s`,
                    transitionProperty: 'opacity',
                    textDecoration: 'none',
                  }}
                  key={index}
                  className={`gtm color-swatch-btn ${
                    colorChosen && element == color ? 'active' : ''
                  }`}
                  role="radio"
                  aria-checked={isColorSelected}
                  aria-label={
                    !notServicable && typeof tag === 'string' && tag
                      ? `${colorName}, ${tag}`
                      : colorName
                  }
                  tabIndex={
                    isColorSelected || (!hasCheckedColor && index === 0)
                      ? 0
                      : -1
                  }
                  data-testid={`ring-color-${element}`}
                  data-buttontype="ring color selector"
                  data-product="Ultrahuman Ring PRO"
                  onMouseEnter={() => setHoveredColor(element as RingProColors)}
                  onMouseLeave={() => setHoveredColor(null)}
                  onFocus={() => setHoveredColor(element as RingProColors)}
                  onBlur={() => setHoveredColor(null)}
                  onClick={(e) => {
                    e.preventDefault();
                    selectColor(element as RingProColors);
                  }}
                >
                  {!notServicable && tag ? <b className="tag">{tag}</b> : null}
                  <CustomImage
                    width={48}
                    height={48}
                    src={RingAirColorNameImages[element].swatch as string}
                    alt=""
                  />
                </a>
              );
            })}
          </div>
          <p
            className={`selected-color-name ${
              hoveredColor || (colorChosen && color) ? '' : 'hint'
            }`}
          >
            {hoveredColor
              ? RingAirColorNameImages[hoveredColor].name
              : colorChosen && color
                ? RingAirColorNameImages[color].name
                : t('ring.productSelection.colorSectionHint')}
          </p>
        </>
      </Section>
    );

    const SizeSelectionSection = (
      <Section
        title={t('ring.productSelection.sizingSection.title')}
        subTitle={t('ring.productSelection.sizingSection.subTitle')}
        // subText="Guessing wrong costs the planet. Use the sizing kit to get it right the first time."

        childClass="sizing-section"
        openInfoModal={openSizeInfoModal}
        // openInfoModalText="How it works"
        // openInfoModalText={
        //   {t('ring.productSelection.sizingSection.openInfoModalText')}
        // }
        state={stepStateOf('size-select')}
        disableScrollToScreen={true}
        sectionClass="size-section"
        ref={showSizeRef}
      >
        <>
          <LargePromptBtnStyledContent
            className={`sizing-kit gtm pb ${
              size == RingSizes.OPEN ? 'active' : ''
            }`}
            data-buttontype="ring size selector"
            data-testid="ring-size-open"
            aria-pressed={size == RingSizes.OPEN}
            data-product="Ultrahuman Ring PRO"
            onClick={() => {
              setSize(RingSizes.OPEN);
              setManualSizeSelectVisible(false);
            }}
            disabled={isOpenSizeDisabled}
          >
            <div className="content-wrapper">
              <CustomImage
                src="web_v2/ring-pro/buy-v2/thumbs/sizing-kit.png"
                alt={tCommon(
                  'productSelectionSection.customImage.alt.sizingKit',
                )}
                width="64"
                height="64"
                loading="lazy"
                className="sizing-kit-img"
              />
              <div className="content">
                <div className="title-bar mb">
                  <div className="title">
                    {/* Sizing Kit (ships in 24 hours) */}
                    {t(
                      'ring.productSelection.sizingSection.sizingKitNew.title',
                    )}{' '}
                    {/* (
                      {t('ring.productSelection.sizingSection.sizingKitNew.titleParenthesis')}
                      ) */}
                  </div>
                  <div className="disclaimer">
                    <div className="price">
                      {isOpenSizeDisabled
                        ? tCommon('productSelectionSection.content.unavailable')
                        : t('ring.FREE')}
                    </div>
                  </div>
                </div>
                <div className="list">
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {t(
                      'ring.productSelection.sizingSection.sizingKitNew.bullets.a',
                    )}
                  </p>
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {t(
                      'ring.productSelection.sizingSection.sizingKitNew.bullets.c',
                    )}
                  </p>
                  <p>
                    <Checkmark className="checkmark" fill="#000000" />
                    {t(
                      'ring.productSelection.sizingSection.sizingKitNew.bullets.d',
                    )}
                  </p>
                </div>
              </div>
            </div>
          </LargePromptBtnStyledContent>

          <div className="manual-size-group">
            {isOpenSizeDisabled ? null : (
              <LargePromptBtnStyledContent
                className={`manual-size manual-size-card gtm ${
                  isManualSizeExpanded ? 'active' : ''
                }`}
                data-buttontype="Ring Size Manual Show"
                data-product="Ring Size Manual Show"
                data-testid="ring-size-manual"
                aria-expanded={isManualSizeExpanded}
                onClick={handleManualSizeSelectShow}
              >
                <div className="content-wrapper">
                  <div className="content">
                    <div className="title-bar">
                      <div className="title">
                        {t(
                          'ring.productSelection.sizingSection.manualSizeText',
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </LargePromptBtnStyledContent>
            )}
            <div
              className={`manual-size-expand ${
                isManualSizeExpanded ? 'expanded' : ''
              }`}
            >
              <div className="manual-size-expand-inner">
                <div className="manual-size-panel">
                  {notServicable !== true && color && shippingEstimate ? (
                    <div className="size-shipping">
                      {pickupEta ? <PickupEtaIcon /> : <ShippingBox />}
                      <p>{pickupEta ?? shippingEstimate}</p>
                    </div>
                  ) : null}
                  {isOpenSizeDisabled ? null : (
                    <p className="manual-size-label">
                      {t(
                        'ring.productSelection.sizingSection.manualSizeSubText',
                      )}
                    </p>
                  )}
                  <div
                    className="sizes"
                    role="group"
                    aria-label={t(
                      'ring.productSelection.sizingSection.title',
                    )}
                  >
                    {Object.values(RingSizes)
                      .filter(
                        (value) =>
                          typeof value === 'number' &&
                          value !== RingSizes.OPEN,
                      )
                      .map((element, index) => {
                        return (
                          <button
                            style={{
                              transitionDelay: `${index * 0.05}s`,
                              transitionProperty: 'opacity',
                            }}
                            key={index}
                            className={`gtm ${
                              size == element ? 'active' : ''
                            }`}
                            data-buttontype="ring size selector"
                            data-product="Ultrahuman Ring PRO"
                            data-testid={`ring-size-${element}`}
                            aria-pressed={size == element}
                            aria-label={tRingPro(
                              'ring-pro.productSelectionSection.sizeSection.optionAriaLabel',
                              { size: element },
                            )}
                            disabled={
                              color &&
                              RingProVariants[color as RingProColors]?.[
                                element as RingSizes
                              ]?.[getEngravingKey(engraving)]?.variantId[
                                store as ShopifyStore
                              ]
                                ? false
                                : true
                            }
                            onClick={() => {
                              if (isOpenSizeDisabled) {
                                handleSizeDisclaimer();
                              }
                              setManualSizeSelectVisible(true);
                              setSize(element as RingSizes);
                            }}
                          >
                            {element}
                          </button>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      </Section>
    );

    const ChargerSelectionSection = (
      <Section
        title={tRingPro('ring-pro.productSelection.chargerSection.title')}
        subTitle={tRingPro('ring-pro.productSelection.chargerSection.subTitle',
        )}
        childClass="charger-section"
        state={stepStateOf('charger-select')}
        disableScrollToScreen={true}
        sectionClass="charger-section"
        ref={showChargerRef}
        // hsaFsaBadge={true}
      >
        <>
          <div
            className="charger"
            role="group"
            aria-label={tRingPro(
              'ring-pro.productSelection.chargerSection.title',
            )}
          >
            {AUTO_PAIR_CHARGER_WITH_RING && color ? (
              (() => {
                const offeredCase = RING_COLOR_TO_CHARGER_MAP[color];
                const offeredIsGold =
                  offeredCase === RingProChargerCaseColor.GOLD;
                const offeredLabel = offeredIsGold
                  ? tRingPro(
                      'ring-pro.productSelectionSection.chargerModal.gold',
                    )
                  : tRingPro(
                      'ring-pro.productSelectionSection.chargerModal.black',
                    );
                const offeredSelected = chargerCaseColor === offeredCase;
                return (
                  <button
                    type="button"
                    className={`charger-option primary ${
                      offeredSelected ? 'active' : ''
                    }`}
                    data-testid="ring-charger-case"
                    aria-label={
                      offeredSelected
                        ? tRingPro(
                            'ring-pro.productSelectionSection.chargerSection.caseSelectedAriaLabel',
                            { label: offeredLabel },
                          )
                        : tRingPro(
                            'ring-pro.productSelectionSection.chargerSection.caseLabel',
                            { label: offeredLabel },
                          )
                    }
                    aria-pressed={offeredSelected}
                    onClick={() => {
                      setChargerCaseColor(offeredCase);
                    }}
                    disabled={!size}
                  >
                    <div className="charger-image">
                      <CustomImage
                        src={`web_v2/ring-pro/buy-v2/thumbs/${
                          offeredIsGold ? 'gold' : 'black'
                        }-charger.png/w_400`}
                        alt={tRingPro(
                          'ring-pro.productSelectionSection.chargerSection.caseAlt',
                          { label: offeredLabel },
                        )}
                        width={200}
                        height={150}
                        style={{ objectFit: 'contain' }}
                      />
                    </div>
                    <div className="charger-label-row">
                      <span className="title">
                        {tRingPro(
                          'ring-pro.productSelectionSection.chargerSection.caseLabel',
                          { label: offeredLabel },
                        )}
                      </span>
                      <span className="charger-price">{t('ring.FREE')}</span>
                    </div>
                  </button>
                );
              })()
            ) : AUTO_PAIR_CHARGER_WITH_RING ? null : (
              <>
                <button
                  type="button"
                  className={`charger-option primary ${
                    chargerCaseColor === RingProChargerCaseColor.GOLD
                      ? 'active'
                      : ''
                  }`}
                  data-testid="ring-charger-gold"
                  aria-pressed={
                    chargerCaseColor === RingProChargerCaseColor.GOLD
                  }
                  onClick={() => {
                    setChargerCaseColor(RingProChargerCaseColor.GOLD);
                  }}
                  disabled={!size}
                >
                  <div className="charger-image">
                    <CustomImage
                      src="web_v2/ring-pro/buy-v2/thumbs/gold-charger.png/w_400"
                      alt={tRingPro('ring-pro.productSelectionSection.chargerSection.caseAlt',
                        {
                          label: tRingPro('ring-pro.productSelectionSection.chargerModal.gold',
                          ),
                        },
                      )}
                      width={200}
                      height={150}
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                  <div className="charger-label-row">
                    <span className="title">
                      {tRingPro('ring-pro.productSelectionSection.chargerSection.caseLabel',
                        {
                          label: tRingPro('ring-pro.productSelectionSection.chargerModal.gold',
                          ),
                        },
                      )}
                    </span>
                    {size && store ? (
                      <span className="charger-price">
                        {(RingProChargerCaseVariants[
                          RingProChargerCaseColor.GOLD
                        ]?.[size]?.price[store as ShopifyStore] ?? 0) === 0 ? (
                          t('ring.FREE')
                        ) : (
                          <>
                            {
                              CurrencySymbol[
                                RingSelectionClient.defaultCurrency.toLowerCase() as Currency
                              ]
                            }
                            {formatPriceWithComma(
                              RingProChargerCaseVariants[
                                RingProChargerCaseColor.GOLD
                              ]?.[size]?.price[store as ShopifyStore] ?? 0,
                            )}
                          </>
                        )}
                      </span>
                    ) : null}
                  </div>
                </button>

                <button
                  type="button"
                  className={`charger-option secondary ${
                    chargerCaseColor === RingProChargerCaseColor.BLACK
                      ? 'active'
                      : ''
                  }`}
                  data-testid="ring-charger-black"
                  aria-pressed={
                    chargerCaseColor === RingProChargerCaseColor.BLACK
                  }
                  onClick={() => {
                    setChargerCaseColor(RingProChargerCaseColor.BLACK);
                  }}
                  disabled={!size}
                >
                  <div className="charger-image">
                    <CustomImage
                      src="web_v2/ring-pro/buy-v2/thumbs/black-charger.png/w_400"
                      alt={tRingPro('ring-pro.productSelectionSection.chargerSection.caseAlt',
                        {
                          label: tRingPro('ring-pro.productSelectionSection.chargerModal.black',
                          ),
                        },
                      )}
                      width={200}
                      height={150}
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                  <div className="charger-label-row">
                    <span className="title">
                      {tRingPro('ring-pro.productSelectionSection.chargerSection.caseLabel',
                        {
                          label: tRingPro('ring-pro.productSelectionSection.chargerModal.black',
                          ),
                        },
                      )}
                    </span>
                    {size && store ? (
                      <span className="charger-price">
                        {(RingProChargerCaseVariants[
                          RingProChargerCaseColor.BLACK
                        ]?.[size]?.price[store as ShopifyStore] ?? 0) === 0 ? (
                          t('ring.FREE')
                        ) : (
                          <>
                            {
                              CurrencySymbol[
                                RingSelectionClient.defaultCurrency.toLowerCase() as Currency
                              ]
                            }
                            {formatPriceWithComma(
                              RingProChargerCaseVariants[
                                RingProChargerCaseColor.BLACK
                              ]?.[size]?.price[store as ShopifyStore] ?? 0,
                            )}
                          </>
                        )}
                      </span>
                    ) : null}
                  </div>
                </button>
              </>
            )}
            <LearnMoreButton
              onClick={openChargerLearnMoreModal}
              aria-label={tRingPro('ring-pro.productSelectionSection.chargerSection.learnMoreAriaLabel',
              )}
            />
          </div>
        </>
      </Section>
    );

    const TradeInSelectionSection = showTradeIn ? (
        <Section
          title={tCommon('productSelectionSection.section.title.trade')}
          subTitle={`${tCommon(
            'productSelectionSection.text.saveUp',
          )} ${getCurrencySymbolFromString(currency)}${formatPriceWithComma(
            tradeInMaxCredit,
          )}.`}
          childClass="trade-in-section"
          state={stepStateOf('trade-in')}
          ref={showTradeInRef}
        >
          <div
            className="trade-in-prompt"
            role="group"
            aria-label={tCommon('productSelectionSection.section.title.trade')}
          >
            <button
              type="button"
              className={`gtm ${tradeInDevice ? 'active' : ''}`}
              data-buttontype="ring trade-in selector"
              data-product="Ultrahuman Ring PRO"
              aria-haspopup="dialog"
              onClick={openTradeInModal}
              aria-label={
                tradeInDevice
                  ? `${tCommon(
                      'productSelectionSection.text.tradeValue',
                    )} ${currencySymbol}${formatPriceWithComma(tradeInCredit)}`
                  : tCommon('productSelectionSection.button.tradeMyRing')
              }
            >
              {tradeInDevice ? (
                <>
                  {tCommon('productSelectionSection.text.tradeValue')}{' '}
                  {currencySymbol}
                  {formatPriceWithComma(tradeInCredit)}
                  <p className="trade-in-details">
                    {tCommon('productSelectionSection.text.ringModel')}{' '}
                    {TRADE_IN_DEVICE_NAMES[tradeInDevice]}
                    <br />
                    {tCommon('productSelectionSection.text.serialNumber')}{' '}
                    {tradeInSerialNumber}
                  </p>
                </>
              ) : (
                tCommon('productSelectionSection.button.tradeMyRing')
              )}
            </button>

            <button
              type="button"
              className={`gtm ${
                tradeInDeclined && !tradeInDevice ? 'active' : ''
              }`}
              data-buttontype="ring trade-in selector"
              data-product="Ultrahuman Ring PRO"
              data-testid="ring-trade-in-decline"
              aria-pressed={!!tradeInDeclined && !tradeInDevice}
              onClick={() => {
                declineTradeIn();
                setTradeInDevice(null);
                setTradeInSerialNumber('');
              }}
              aria-label={tCommon(
                'productSelectionSection.button.donWantTrade',
              )}
            >
              {tCommon('productSelectionSection.button.donWantTrade')}
            </button>
          </div>
        </Section>
      ) : null;

    // Ring PRO does not launch with engraving; may support engraving in the future.
    // const EngravingSelectionSection = showEngravingSection ? (
    //   <Section
    //     title={t('ring.productSelection.engravingSection.title')}
    //     subTitle={t('ring.productSelection.engravingSection.subTitle')}
    //     childClass="engraving-section"
    //     show={!!size}
    //     ref={engravingRef}
    //   >
    //     <div className="engraving-prompt">
    //       <button
    //         className={`border-radius-change gtm ${engraving ? 'active' : ''}`}
    //         data-buttontype="ring engraving selector"
    //         data-product="Ultrahuman Ring PRO"
    //         disabled={
    //           size &&
    //             RingProVariants[color as RingProColors]?.[size]?.[
    //               getEngravingKey(true)
    //             ]?.variantId[store as ShopifyStore]
    //             ? false
    //             : true
    //         }
    //         onClick={() => {
    //           setEngraving(true);
    //         }}
    //       >
    //         {/* Add Engraving */}
    //         {t('ring.productSelection.engravingSection.engraving.title')}
    //         <p className="disclaimer-engraving">
    //           {/* Add custom engraving for just {currencySymbol} */}
    //           {t('ring.productSelection.engravingSection.engraving.subTitle')}{' '}
    //           {currencySymbol}
    //           {formatPriceWithComma(EngravingPrice[store as ShopifyStore])}{' '}
    //           {t('ring.EXTRA')}
    //           <br />
    //           {/* *Engraving orders take 7 days longer to ship */}
    //           {t('ring.productSelection.engravingSection.engraving.highlight')}
    //         </p>
    //       </button>
    //
    //       {engraving && (
    //         <div className="engraving-content">
    //           {/* <h5>Personalise your ring</h5> */}
    //           {/* <p className='disclaimer-engraving'>
    //               Custom engrave the ring at $200 and make it an exceptionally
    //               unique gift
    //             </p> */}
    //           <div className="input-btn-container">
    //             <input
    //               id="engraving-input-field"
    //               onChange={(e) => {
    //                 const input = (e.target.value as string)
    //                   .trimStart()
    //                   .trimEnd();
    //                 if (
    //                   input != '' &&
    //                   !/^[A-Za-z0-9!@#$%^&*()_\-+={}[\];:'"<,>.?/|\\`~]+$/.test(
    //                     input,
    //                   )
    //                 )
    //                   return;
    //                 setEngravingMessage(input);
    //               }}
    //               // placeholder="eg. Jon Snow"
    //               placeholder={t('ring.productSelection.engravingSection.engraving.placeHolder')}
    //               type="text"
    //               max={8}
    //               maxLength={8}
    //               value={engravingMessage ?? ''}
    //               disabled={
    //                 size &&
    //                   RingProVariants[color as RingProColors]?.[size]?.[
    //                     getEngravingKey(engraving)
    //                   ]?.variantId[store as ShopifyStore]
    //                   ? false
    //                   : true
    //               }
    //             />
    //
    //             <div className="max-length">
    //               <span style={{ textAlign: 'left' }}>
    //                 {/* Max. 8 characters */}
    //                 {t('ring.productSelection.engravingSection.engraving.maxLength')}
    //               </span>
    //               <span style={{ textAlign: 'right' }}>
    //                 {/* (alphanumeric & some special characters supported) */}
    //                 (
    //                 {t('ring.productSelection.engravingSection.engraving.support')}
    //                 )
    //               </span>
    //             </div>
    //           </div>
    //         </div>
    //       )}
    //
    //       <button
    //         className={`gtm ${engraving === false ? 'active' : ''}`}
    //         data-buttontype="ring engraving selector"
    //         data-product="Ultrahuman Ring PRO"
    //         disabled={
    //           size &&
    //             RingProVariants[color as RingProColors]?.[size]?.[
    //               getEngravingKey(engraving)
    //             ]?.variantId[store as ShopifyStore]
    //             ? false
    //             : true
    //         }
    //         onClick={() => {
    //           setEngraving(false);
    //           setEngravingMessage(null);
    //         }}
    //       >
    //         {/* I don&apos;t want the engraving */}
    //         {t('ring.productSelection.noEngraving')}
    //       </button>
    //     </div>
    //   </Section>
    // ) : null;

    const ClueAddonSection = isClueFlow ? (
      <Section
        title={t('ring.productSelection.clueSection.title')}
        subTitle={t('ring.productSelection.clueSection.subTitle')}
        ref={powerplugRef}
        childClass="powerPlugs-section"
        state={stepStateOf('clue-addon')}
        // subText={
        //   {t('ring.productSelection.powerPlugsSection.subText')}
        // }
      >
        <PowerPlugSubsection
          title={t('ring.CLUE_PLUS')}
          description={t('ring.productSelection.clueSection.clue.description')}
          image="web_v2/powerplugs/icons/clue.png"
        >
          <LargePromptBtnStyledContent
            className={`clue-addon gtm ${
              clueCart === 'Year1' ? 'active' : ''
            } paddingTop`}
            data-buttontype="clue addon"
            data-product="Ultrahuman Ring PRO"
            data-testid={`ring-clue-1-year`}
            aria-pressed={clueCart === 'Year1'}
            onClick={() => {
              setClueCart('Year1');
            }}
            disabled={
              size &&
              engraving !== null &&
              RingProVariants[color as RingProColors]?.[size]?.[
                getEngravingKey(engraving)
              ]?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <div className="label">
              {t('ring.productSelection.clueSection.clue.optionA.tag')}
            </div>
            <div className="content-wrapper">
              <div className="content">
                <div className="title-bar">
                  <div className="title">
                    <span>
                      <strong>
                        {t(
                          'ring.productSelection.clueSection.clue.optionA.head',
                        )}
                      </strong>
                    </span>
                  </div>
                  <div className="disclaimer">
                    <div className="price-display">
                      <span className="striked price">
                        {currencySymbol}
                        {formatPriceWithComma(
                          ClueShopifyMap[store as ShopifyStore]?.Year1?.price ||
                            0,
                        )}
                      </span>
                      <div className="price">
                        {currencySymbol}
                        {(() => {
                          const price =
                            ClueShopifyMap[store as ShopifyStore]?.Year1
                              ?.discountedPrice || 0;
                          const priceStr = price.toFixed(2);
                          const [whole, decimal] = priceStr.split('.');
                          const showDecimal = decimal !== '00';
                          return (
                            <>
                              {formatPriceWithComma(parseInt(whole))}
                              {showDecimal && (
                                <span
                                  style={{
                                    fontSize: '1.2rem',
                                    fontWeight: '400',
                                    color: '#000',
                                    letterSpacing: '-0.48px',
                                  }}
                                >
                                  .{decimal}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
                {store === ShopifyStore.US ? (
                  <div className="hsa-fsa-badge">
                    <FSAHSAComponent />
                  </div>
                ) : null}
              </div>
            </div>
          </LargePromptBtnStyledContent>

          <button
            className={`no-ultrahumanx gtm ${
              clueCart === false ? 'active' : ''
            }`}
            data-buttontype="cno-pro selection"
            data-product="Ultrahuman Ring PRO"
            data-default-option="true"
            data-crawler-recommended="true"
            aria-pressed={clueCart === false}
            aria-label={tCommon('productSelectionSection.button.ariaLabelClue')}
            onClick={() => {
              setClueCart(false);
            }}
            disabled={
              size &&
              engraving !== null &&
              RingProVariants[color as RingProColors]?.[size]?.[
                getEngravingKey(engraving)
              ]?.variantId[store as ShopifyStore]
                ? false
                : true
            }
          >
            <span>
              <strong>{t('ring.productSelection.clueSection.clue.no')}</strong>
            </span>
          </button>

          <LearnMoreButton onClick={openClueModal} />
        </PowerPlugSubsection>
      </Section>
    ) : null;

    const MaxSelectionSection = maxConfig ? (
      <Section
        title={t('ring.productSelection.maxSection.title')}
        subTitle={
          isMaxUsCopy(country)
            ? t('ring.productSelection.maxSection.us.subTitle')
            : t('ring.productSelection.maxSection.subTitle')
        }
        childClass="max-section"
        infoIcon={<QuestionMarkCircleIcon style={{ marginTop: '1px' }} />}
        state={stepStateOf('max')}
        ref={coverageRef}
        openInfoModal={openMaxInfoModal}
      >
        <MaxSelectionContent
          config={maxConfig}
          currencySymbol={currencySymbol}
          value={maxCart}
          onChange={setMaxCart}
          productName="Ultrahuman Ring PRO"
          onLearnMore={openMaxInfoModal}
          onCoverageInfo={openUhxInfoModal}
          tradeInBonus={showTradeIn ? UHX_BONUS[store as ShopifyStore] ?? 0 : 0}
          disabled={
            !(
              size &&
              RingProVariants[color as RingProColors]?.[size]?.[
                getEngravingKey(engraving)
              ]?.variantId[store as ShopifyStore]
            )
          }
        />
      </Section>
    ) : null;

    // Shown from page load, dimmed like the other steps until it is reached.
    const summaryState = stepStateOf('order-overview');

    const OverviewSection =
      notServicable === true ? (
        <Fragment>{NotServicableMessage}</Fragment>
      ) : (
        <Fragment>
            {/* Locked steps are inert; the wrapper catches the click and takes
                the shopper to the step still waiting for an answer. */}
            <div
              className={summaryState === 'locked' ? 'summary-locked-hit' : undefined}
              onClick={
                summaryState === 'locked'
                  ? () => RingSelectionClient.goToNextStep()
                  : undefined
              }
            >
              <Section
                childClass="cart-summary"
                title={t('ring.productSelection.cartSummary.head')}
                state={summaryState}
                ref={cartSummaryRef}
              >
                <div className="card" id="order-summary-card">
                  {effectiveDiscount.type === 'hero' && !isVerifiedHeroDiscount ? (
                  <div
                  className="disclaimer"
                  style={{
                    marginBottom: '20px',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: '-16px',
                  }}
                  >
                  <InfoIcon
                    fill="#00000077"
                    style={{
                      flexShrink: '0',
                      marginRight: '8px',
                      alignSelf: 'flex-start',
                      transform: 'translateY(2px)',
                    }}
                  />
                  <p>
                    {t('ring.productSelection.cartSummary.heroDisclaimer.a')}
                    <CustomLink
                      href="/privacyPolicy"
                      target="_blank"
                      style={{
                        color: 'inherit !important',
                        textDecoration: 'underline',
                      }}
                    >
                      {t(
                        'ring.productSelection.cartSummary.heroDisclaimer.b',
                      )}
                    </CustomLink>
                    {t('ring.productSelection.cartSummary.heroDisclaimer.c')}
                  </p>
                  {/* Heroes Special Pricing Verification: By purchasing, you consent
                  to provide ID or proof of eligibility if requested. We collect
                  only what&apos;s needed, delete it after use, and handle your
                  data per applicable privacy laws. Please review our Privacy
                  Policy for details */}
                  </div>
                  ) : null}

                  <div className="content">
                  <h5 data-testid="order-summary-ring">
                  {/* Ultrahuman Ring PRO */}
                  {tRingPro('ring-pro.contents.data.Ring')}{' '}
                  {/* {engraving ? '(with engraving)' : null} */}
                  {engraving
                    ? `(${t('ring.contents.WITH_ENGRAVING')})`
                    : null}
                  </h5>
                  <p className="size" data-testid="order-summary-size">
                  {color ? RingAirColorNameImages[color].name : ''}
                  {size
                    ? `, ${
                        size === RingSizes.OPEN
                          ? t('ring.productSelection.cartSummary.sizingKitIn')
                          : t('ring.SIZE') + ` ${size}`
                      }`
                    : ''}
                  </p>
                  <div className="price-breakup">
                  <p>{tRingPro('ring-pro.contents.data.Ring')}</p>
                  <p>
                    {currencySymbol}
                    {formatPriceWithComma(basePrice)}
                  </p>
                  </div>
                  {effectiveDiscount.serverAmount > 0 ? (
                  <div className="price-breakup">
                    <p>{effectiveDiscount.label}</p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(effectiveDiscount.serverAmount)}
                    </p>
                  </div>
                  ) : null}
                  {maxConfig && hasMaxSelected(maxCart) ? (
                  <div
                    className="price-breakup"
                    data-testid="order-summary-max"
                  >
                    <p>
                      {t('ring.productSelection.maxSection.name')} -{' '}
                      {maxPlanCopy.label(maxConfig, maxCart)}
                    </p>
                    <p>
                      +{currencySymbol}
                      {formatPriceWithComma(ultraHumanPrice)}
                      {maxPlanCopy.suffix(maxConfig, maxCart)}
                    </p>
                  </div>
                  ) : null}
                  {isClueFlow && clueCart ? (
                  <div className="price-breakup">
                    <p>{t('ring.productSelection.cartSummary.clue1Year')}</p>
                    <p>
                      {currencySymbol}
                      {formatPriceWithComma(
                        ClueShopifyMap[store as ShopifyStore]?.Year1?.price ||
                          0,
                      )}
                    </p>
                  </div>
                  ) : null}
                  {respiratoryCart ? (
                  <div className="price-breakup">
                    <p>
                      {tCommon(
                        'productSelectionSection.text.respiratoryHealthPowerplug',
                      )}
                    </p>
                    <p>
                      {currencySymbol}
                      {formatPriceWithComma(
                        RespiratoryShopifyMap[store as ShopifyStore]?.Year1
                          ?.price || 0,
                      )}
                    </p>
                  </div>
                  ) : null}
                  {cnoProCart ? (
                  <div className="price-breakup">
                    <p>
                      {cnoProCart === 'Year2'
                        ? `${t('ring.CNO_PRO')} - 2 years`
                        : `${t('ring.CNO_PRO')} - 1 year`}
                    </p>
                    <p>
                      +{currencySymbol}
                      {formatPriceWithComma(
                        CnoProShopifyMap[store as ShopifyStore][cnoProCart]
                          .price,
                      )}
                    </p>
                  </div>
                  ) : null}
                  {chargerCaseColor ? (
                  <div
                    className="price-breakup"
                    data-testid="order-summary-case"
                  >
                    <p>
                      {tRingPro(
                        'ring-pro.productSelectionSection.chargerSection.caseLabel',
                        {
                          label:
                            chargerCaseColor === RingProChargerCaseColor.GOLD
                              ? tRingPro(
                                  'ring-pro.productSelectionSection.chargerModal.gold',
                                )
                              : tRingPro(
                                  'ring-pro.productSelectionSection.chargerModal.black',
                                ),
                        },
                      )}
                    </p>
                    <p>
                      {chargerPrice > 0 ? (
                        <>
                          +{currencySymbol}
                          {formatPriceWithComma(chargerPrice)}
                        </>
                      ) : (
                        t('ring.FREE')
                      )}
                    </p>
                  </div>
                  ) : null}
                  {isLuminaryFlow ? (
                    <div className="price-breakup">
                      <p>
                        {t(
                          'ring.productSelection.cartSummary.luminaryCnoPro6Months',
                        )}
                      </p>
                      <p>{t('ring.FREE')}</p>
                    </div>
                  ) : null}
                  {tradeInDevice && tradeInCredit > 0 ? (
                  <div
                    className="price-breakup"
                    data-testid="order-summary-trade-in"
                  >
                    <p>
                      {tCommon(
                        'productSelectionSection.text.tradeCreditVerification',
                      )}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(tradeInCredit)}
                    </p>
                  </div>
                  ) : null}
                  {affiliateDiscount > 0 &&
                  (effectiveDiscount.type === 'affiliate' ||
                  effectiveDiscount.type === 'server+affiliate') ? (
                  <div className="price-breakup">
                    <p>
                      {/* Affiliate discount */}
                      {t('ring.productSelection.cartSummary.priceBreakup.b')}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(affiliateDiscount)}
                    </p>
                  </div>
                  ) : null}
                  {referral && effectiveDiscount.type === 'referral' ? (
                  <div className="price-breakup">
                    <p>
                      {/* Affiliate discount */}
                      {t('ring.productSelection.cartSummary.priceBreakup.b')}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(referral.discount)}
                    </p>
                  </div>
                  ) : null}
                  {effectiveDiscount.type === 'hero' ? (
                  <div className="price-breakup">
                    <p>
                      {/* "Hero Discount" → "GovX/ID.me Discount" once verified */}
                      {isGovxDiscount
                        ? t('ring.productSelection.cartSummary.priceBreakup.govx')
                        : isIdmeDiscount
                          ? t('ring.productSelection.cartSummary.priceBreakup.idme')
                          : t('ring.productSelection.cartSummary.priceBreakup.c')}
                      {!isVerifiedHeroDiscount ? (
                        <span
                          style={{ display: 'block', fontSize: '1.2rem' }}
                        >
                          {t(
                            'ring.productSelection.cartSummary.priceBreakup.d',
                          )}
                        </span>
                      ) : null}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(effectiveDiscount.amount)}
                    </p>
                  </div>
                  ) : null}
                  {isClueFlow && clueCart ? (
                  <div className="price-breakup">
                    <p>
                      {t('ring.productSelection.cartSummary.clueDiscount')}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(
                        (ClueShopifyMap[store as ShopifyStore]?.Year1
                          ?.price || 0) -
                          (ClueShopifyMap[store as ShopifyStore]?.Year1
                            ?.discountedPrice || 0),
                      )}
                    </p>
                  </div>
                  ) : null}
                  {isLuminaryFlow && luminaryDiscount > 0 ? (
                    <div className="price-breakup">
                      <p>
                        {t('ring.productSelection.cartSummary.luminaryDiscount')}
                      </p>
                      <p>
                        -{currencySymbol}
                        {formatPriceWithComma(luminaryDiscount)}
                      </p>
                    </div>
                  ) : null}
                  {isRespiratoryFlow && respiratoryCart ? (
                  <div className="price-breakup">
                    <p>
                      {tCommon(
                        'productSelectionSection.text.respiratoryHealthPowerplug2',
                      )}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(
                        (RespiratoryShopifyMap[store as ShopifyStore]?.Year1
                          ?.price || 0) -
                          (RespiratoryShopifyMap[store as ShopifyStore]?.Year1
                            ?.discountedPrice || 0),
                      )}
                    </p>
                  </div>
                  ) : null}
                  {effectiveDiscount.type === 'birthday' ? (
                  <div className="price-breakup">
                    <p>
                      {tCommon(
                        'productSelectionSection.text.birthdayDiscount',
                      )}
                      {Math.round((effectiveDiscount.amount / price) * 100)}
                      %)
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(effectiveDiscount.amount)}
                    </p>
                  </div>
                  ) : null}
                  {effectiveDiscount.type === 'ringPromo' ? (
                  <div className="price-breakup">
                    <p>
                      {ringCampaign?.title || 'Special'}{' '}
                      {tCommon('productSelectionSection.text.offerApplied')}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(effectiveDiscount.amount)}
                    </p>
                  </div>
                  ) : null}
                  {!(
                  !(store === ShopifyStore.GB || store === ShopifyStore.EU) &&
                  affiliateDiscount
                  ) &&
                  defaultOfferText &&
                  defaultOfferDiscount ? (
                  <div className="price-breakup">
                    <p>
                      {defaultOfferText}{' '}
                      {tCommon('productSelectionSection.text.offer')}
                    </p>
                    <p>
                      -{currencySymbol}
                      {formatPriceWithComma(defaultOfferDiscount as number)}
                    </p>
                  </div>
                  ) : null}
                  </div>

                  <div className="total">
                  <div className="total-bar">
                    <h5>
                      {/* Total */}
                      {t('ring.TOTAL')}{' '}
                    </h5>{' '}
                    <p>
                      {currencySymbol}
                      {formatPriceWithComma(effectivePrice)}
                    </p>
                  </div>
                  {getShippingAndTaxDisclaimerMap(tCart)[
                    store as ShopifyStore
                  ] ? (
                    <p
                      className="taxes"
                      style={{
                        marginBottom: '4px',
                      }}
                    >
                      {/* Best Buy pickup chosen: free pickup. Otherwise the
                          store's usual shipping and tax line. */}
                      {pickupEta
                        ? tPickup('ring-pro.bestBuyPickup.summaryPickup')
                        : getShippingAndTaxDisclaimerMap(tCart)[
                            store as ShopifyStore
                          ]}
                    </p>
                  ) : null}

                  {shippingEstimate ? (
                    <p className="taxes shipping-estimate">
                      {pickupEta ?? shippingEstimate}
                    </p>
                  ) : null}

                  {canAddToCart ? (
                    <PrimaryButton
                      className="checkout"
                      disabled={isLoading}
                      aria-busy={isLoading}
                      data-buttontype="add to cart"
                      data-product="Ultrahuman Ring PRO"
                      data-testid="ring-add-to-cart"
                      data-variantId={
                        color && size && currency
                          ? RingProVariants[color]?.[size]?.[
                              getEngravingKey(engraving)
                            ]?.variantId[store as ShopifyStore]
                          : ''
                      }
                      onClick={handleCheckout}
                    >
                      {/* Add to cart */}
                      {isLoading ? (
                        <DotSpinner color="#000000" />
                      ) : (
                        t('ring.productSelection.cartSummary.addToCart')
                      )}
                    </PrimaryButton>
                  ) : null}

                  {/* <div className="" style={{ marginBottom: '16px' }}>
                  <HeroConsentDisclaimer align="flex-start" icon />
                  </div> */}

                  {pathname?.includes('/us/') ? (
                    <div className="hsa-fsa-disclaimer">
                      <CustomImage
                        src="web_v2/icons/hsa-fsa/hsa-fsa-icon-black.svg"
                        alt={tCommon(
                          'productSelectionSection.customImage.alt.hsaFsa',
                        )}
                        width="248"
                        height="81"
                      />
                      <span>
                        <b>
                          {tCommon('productSelectionSection.content.hsaFsa')}
                        </b>{' '}
                        {tCommon(
                          'productSelectionSection.text.eligibleSaveMore',
                        )}
                      </span>
                    </div>
                  ) : null}

                  </div>
                  {resolvedOffersData && resolvedOffersData.length > 0 && (
                  <div className="payment-offers">
                  {resolvedOffersData.map((offer, index) => (
                    <Fragment key={index}>
                      {index > 0 && <div className="offer-divider" />}
                      <div className="offer-item">
                        <div className="offer-icon">
                          {offer.image?.[0] && (
                            <CustomImage
                              src={offer.image[0]}
                              alt=""
                              width="24"
                              height="24"
                            />
                          )}
                        </div>
                        <p>{offer.title}</p>
                      </div>
                    </Fragment>
                  ))}
                  </div>
                  )}

                  {store &&
                  ![
                  ShopifyStore.IN,
                  ShopifyStore.AE,
                  ShopifyStore.GB,
                  ShopifyStore.EU,
                  ].includes(store as ShopifyStore) && (
                  <p className="disclaimer">
                    {/* *Please note that additional local taxes or carrier fees may
                  apply upon delivery */}
                    {t(
                      'ring.productSelection.cartSummary.benefits.disclaimer',
                    )}
                  </p>
                  )}
                  {/* <PlantTreesBanner /> */}
                </div>
                <VitaminAngelsBanner />
              </Section>
            </div>
        </Fragment>
      );

    const productSections: Partial<Record<RingBuyStepId, ReactNode>> = {
      'color-picker': ColorSelectionSection,
      'size-select': (
        <>
          {SizeSelectionSection}
          {/* Always shown under Size on the US store; the pickup card greys
              out when pickup is not possible for the selection. */}
          {deliveryStepVisible ? (
            <BuyPagePickup
              color={color}
              size={size}
              engraving={engraving}
              state={stepStateOf('delivery')}
              shipsBy={shippingEstimate}
              onChooseSize={chooseSizeForPickup}
              onChooseColor={chooseColorForPickup}
              sectionRef={sectionRefs.deliveryRef}
            />
          ) : null}
        </>
      ),
      'charger-select': ChargerSelectionSection,
      'trade-in': TradeInSelectionSection,
      'clue-addon': ClueAddonSection,
      max: MaxSelectionSection,
      'order-overview': OverviewSection,
    };

    // Clue is a secondary offer; MAX follows allowMax.
    const sectionOrder = RING_PRO_BUY_STEP_ORDER.filter(
      (id) =>
        (id !== 'max' || policy.allowMax) &&
        (id !== 'clue-addon' || policy.showClue),
    );

    return (
      <div
        className={
          className ? `${styles.buySection} ${className}` : styles.buySection
        }
      >
        <div className="buy-section title-info-content">
          {/* mobile section */}
          <div className="title-container">
            <H2 as="h1">
              <>
                {buySectionTitle}
                <span>
                  {tRingPro('ring-pro.productSelectionSection.titleSuffix.withProCharging',
                  )}
                </span>
              </>
            </H2>
          </div>
          {notServicable === true ? null : (
            <div className={styles.priceContentContainer}>
              {/* Price listing */}
              <div className="price-line">
                {(priceDrop > 0 ||
                  defaultStrikedPrice ||
                  effectiveDiscount.amount > 0 ||
                  luminaryDiscount > 0) && (
                  <span className="strike-through">
                    {currencySymbol}
                    {formatPriceWithComma(strikeBasePrice)}
                  </span>
                )}
                <span className="current-price">
                  {currencySymbol}
                {formatPriceWithComma(ringPriceAfterDiscounts)}
                </span>
                {installmentPrice && installmentConfig && (
                  <span className="installment">
                    or&nbsp;&nbsp;{currencySymbol}
                    {formatPriceWithComma(installmentPrice)}/
                    {installmentConfig.label}&nbsp;
                    <span className="installment-symbol">&plusmn;</span>
                  </span>
                )}
                {/* Trade-in lives in the sequential funnel on PRO, not above the price. */}
                <SplitPaymentsRow data={resolvedOffersData ?? []} />
              </div>
              <TierProgressBar />
              {hasOfferContent && (
                <div
                  className="offerFlex"
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    width: '100%',
                  }}
                >
                  {effectiveDiscount.type === 'affiliate' ||
                  effectiveDiscount.type === 'server+affiliate' ? (
                    <p className="offerTag transparent">
                      <span className=" ">
                        {searchParams?.get('affiliateCode') === 'retouch' ? (
                          <>
                            {Number(
                              affiliateDetails?.discount_configs[
                                effectiveStoreForAffiliate as ShopifyStore
                              ]?.RING_PRO?.value ??
                                affiliateDetails?.discount_configs[
                                  effectiveStoreForAffiliate as ShopifyStore
                                ]?.RING?.value,
                            )}
                            {tCommon('productSelectionSection.text.offApplied')}
                          </>
                        ) : (
                          <>
                            {Number(
                              affiliateDetails?.discount_configs[
                                effectiveStoreForAffiliate as ShopifyStore
                              ]?.RING_PRO?.value ??
                                affiliateDetails?.discount_configs[
                                  effectiveStoreForAffiliate as ShopifyStore
                                ]?.RING?.value,
                            )}
                            {tCommon(
                              'productSelectionSection.text.offAppliedVia',
                            )}{' '}
                            {affiliateDetails?.discount_configs[
                              effectiveStoreForAffiliate as ShopifyStore
                            ]?.RING_PRO?.code ??
                              affiliateDetails?.discount_configs[
                                effectiveStoreForAffiliate as ShopifyStore
                              ]?.RING?.code}
                            {/* work affiliate */}
                            {searchParams?.get('workAffiliate')
                              ? ` ${tCommon(
                                  'productSelectionSection.text.ultrahuman',
                                )}`
                              : ''}
                          </>
                        )}
                      </span>
                      <br />
                    </p>
                  ) : null}{' '}
                  {effectiveDiscount.type === 'referral' && referral ? (
                    <p className="offerTag">
                      <span>
                        <span className="offerApplied">
                          {referral.discountCode}
                        </span>{' '}
                        {tCommon('productSelectionSection.text.offerApplied')}
                      </span>
                    </p>
                  ) : null}
                  {effectiveDiscount.type === 'hero' &&
                  heroCategoryOfferTagMap[heroCategory] ? (
                    <p className="offerTag transparent">
                      <span>
                        {heroCategoryOfferTagMap[heroCategory]}
                      </span>
                    </p>
                  ) : null}
                  {effectiveDiscount.serverAmount > 0 &&
                  effectiveDiscount.priceTagText ? (
                    <p className="offerTag transparent">
                      <span>{effectiveDiscount.priceTagText}</span>
                    </p>
                  ) : null}
                  {isClueFlow && country && store ? (
                    <p
                      className="offerTag"
                      style={{
                        width: '100%',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <span>
                        <ClueLogo />
                        {tCommon(
                          'productSelectionSection.text.includesYearClue',
                        )}{' '}
                        {currencySymbol}
                        {
                          ClueShopifyMap[store as ShopifyStore].Year1
                            .discountedPrice
                        }{' '}
                        {tCommon('productSelectionSection.text.worth')}{' '}
                        {currencySymbol}
                        {ClueShopifyMap[store as ShopifyStore].Year1.price})
                      </span>
                    </p>
                  ) : null}
                  {isRespiratoryFlow && country && store ? (
                    <p
                      className="offerTag"
                      style={{
                        width: '100%',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <span>
                        <RespiratoryLogo />
                        {tCommon(
                          'productSelectionSection.text.includesYearRespiratory',
                        )}{' '}
                        {currencySymbol}
                        {
                          RespiratoryShopifyMap[store as ShopifyStore].Year1
                            .price
                        }
                        )
                      </span>
                    </p>
                  ) : null}
                  {isOvusenseFlow && country && store && isCnoProActive ? (
                    <button
                      onClick={openCnoPro}
                      className="offerTag"
                      style={{
                        width: '100%',
                        flexWrap: 'nowrap',
                      }}
                    >
                      <CustomImage
                        src="web_v2/icons/cno-pro-addon-small.png"
                        alt="cno-pro-addon-small"
                        width="24"
                        height="24"
                      />
                      <span>
                        {/* <ClueLogo /> */}
                        {tCommon(
                          'productSelectionSection.text.includesYear',
                        )}{' '}
                        {getValue(
                          'Cycle & Ovulation Pro',
                          'Cycle & Ovulation Plus',
                        )}{' '}
                        {tCommon('productSelectionSection.text.worth')}{' '}
                        {currencySymbol}
                        {CnoProShopifyMap[store as ShopifyStore].Year1.price}
                        )
                        <ChevronRight
                          style={{
                            width: 'auto',
                            height: '12px',
                            paddingLeft: '4px',
                            flexShrink: '0',
                          }}
                        />
                      </span>
                    </button>
                  ) : null}
                  {effectiveDiscount.type === 'birthday' ? (
                    <p className="offerTag">
                      <Image
                        src={BirthdayIcon}
                        alt="birthday_icon"
                        style={{
                          borderRadius: '0',
                        }}
                      />
                      <span>
                        {tCommon(
                          'productSelectionSection.text.birthdayDiscountApplied',
                        )}
                      </span>
                    </p>
                  ) : null}
                  {effectiveDiscount.type === 'ringPromo' ? (
                    <p className="offerTag special-offer">
                      <span>
                        {ringCampaign?.title || 'SPECIAL'}{' '}
                        {tCommon('productSelectionSection.text.offerApplied2')}
                      </span>
                    </p>
                  ) : null}
                  {defaultOfferText && !(affiliateDetails || referral) ? (
                    <p className="offerTag">
                      <span>
                        <span className="offerApplied">{defaultOfferText}</span>{' '}
                        {tCommon('productSelectionSection.text.offerApplied')}
                      </span>
                    </p>
                  ) : null}
                </div>
              )}

              {color && shippingEstimate ? (
                <p className="shipping-estimate">
                  {pickupReadyBy ? <PickupEtaIcon /> : null}
                  {pickupEta ?? shippingEstimate}
                </p>
              ) : null}
              {/* Pickup chosen: the line above already says so. */}
              {notServicable || pickupEta ? null : (
                <SameDayPickupLine engraving={engraving} />
              )}

              {policy.showBenefitBanner ? <OptumBenefitBanner /> : null}
              <GiftCardAppliedBanner />

              {!resolvedOffersData ? (
                <p className="description">
                  {/* A comfortable and sleek smart ring that tracks your sleep,
                  movement and recovery trends, nudging you to make healthier
                  choices every day. */}
                  {description}
                </p>
              ) : null}

              {/* <ViewCount /> */}

              {isLuminaryFlow && store ? (
                <button
                  onClick={openCnoPro}
                  className="offerTag luminaryOffer"
                >
                  <span>
                    <CustomImage
                      src={getCompressedAssetUrl(
                        '/web_v2/site-static/luminary/luminary-icon-color.svg',
                      )}
                      alt="Luminary"
                      width={16}
                      height={16}
                    />
                    {tCommon(
                      'productSelectionSection.text.includesSixMonthsCnoPro',
                    )}
                    <ChevronRight />
                  </span>
                </button>
              ) : secondaryOfferData ? (
                <OffersCarousel data={secondaryOfferData} leftAligned={true} />
              ) : null}
              {/* {secondaryOfferData && (
            <SecondaryOfferComponent data={secondaryOfferData} />
          )} */}
            </div>
          )}
        </div>

        <MobileCarouselSection className="carousel-section">
          <RingProProductCarousel
            items={[
              ...((RingSelectionClient.color
                ? RingProCarouselData[RingSelectionClient.color]
                : RingProCarouselData[RingProColors.GOLD]) ?? []),
            ]}
            selectedColor={RingSelectionClient.color || RingProColors.GOLD}
            sticky={false}
            footerItems={[
              {
                title: (
                  <>
                    {isUSLocale && (
                      <>
                        {tRingPro(
                          'ring-pro.productSelectionSection.carousel.hsaFsaEligible',
                        )}
                        {'\u00A0\u00A0'}&bull;{'\u00A0\u00A0'}
                      </>
                    )}
                    {tRingPro(
                      'ring-pro.productSelectionSection.carousel.upTo15DayBattery',
                    )}
                  </>
                ),
              },
            ]}
          />
        </MobileCarouselSection>

        {/* Explore Styles Section  */}
        {/* {width > 786 ? ExploreStylesSection : null} */}

        {sectionOrder.map((id) => (
          <Fragment key={id}>{productSections[id]}</Fragment>
        ))}

        {/* {effectiveDiscount.type === 'hero' ? (
          <Section
            childClass="cart-summary"
            ref={heroDisclaimerRef}
            show={canAddToCart}
          >
            <div className="card">
            </div>
          </Section>
        ) : null} */}

        {isTradeInModalVisible && (
          <TradeInModal
            onClose={() => setIsTradeInModalVisible(false)}
            contextSource="ring_pro"
          />
        )}
      </div>
    );
};

export { ProductSelectionSection as RingProProductSelectionSection };
